import {
  CheckBadgeIcon,
  LockClosedIcon,
  PaperAirplaneIcon,
  XMarkIcon
} from "@heroicons/react/24/outline";
import { FC, useState } from "react";
import { OfficeButton, OfficeHeader, Show, Tabs } from "components";
import { useAppLocation, useCurrentUser } from "_graphql/cache/auth";

import AcceptAgreementContainer from "./accept-terms-of-agreement";
import AddParticipantsContainer from "./add-participants";
import CloseOfferContainer from "./close-offer";
import { DocumentCheckIcon } from "@heroicons/react/24/solid";
import FleetPicker from "./components/fleet-picker";
import OfferAssociates from "./associates";
import OfferOverView from "./overview";
import OfferParticipation from "./participation";
import PublishUnpublishOfferContainer from "./components/publish-unpublish-offer";
import { PublishedStatus } from "_graphql/__generated__/globalTypes";
import SendEmailsContainer from "./send-emails";
import { useOffer } from "hooks/data/use-offers";
import { useUrlState } from "utils";

type Props = {};

const tabs = [
  { name: "Offer Participants", href: "participants" },
  { name: "Participant's Associates", href: "associates" },
];

const OfferPage: FC<Props> = () => {
  const currentUser = useCurrentUser();
  const [modal, setModal] = useUrlState("modal");
  const [orderStatus] = useUrlState("orderStatus");
  const { params } = useAppLocation();
  const offerId = params?.offer || "";

  const { offer, refetch } = useOffer({
    offer_id: offerId,
  });

  // const [isOfferPublished, setIsOfferPublished] = useState(false);
  const isOfferPublished =
    offer?.offer_published?.published_status === PublishedStatus.PUBLISHED;

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
      <OfficeHeader
        renderActions={() => (
          <>
            <FleetPicker offer={offer} />
            <Show if={Boolean(currentUser?.employee?.accept_waiver_aggreement)}>
              <OfficeButton variant={isOfferPublished ? "danger" : "primary"} size="sm" onClick={() => setModal("publish")}>
                {isOfferPublished ? (
                  <XMarkIcon
                    className="w-5 h-5 mr-2" />
                ) : (
                  <CheckBadgeIcon className="w-5 h-5 mr-2" />
                )}
                <span className="inline text-xs sm:hidden">
                  {isOfferPublished ? "Unpub." : "Publish"}
                </span>
                <span className="hidden sm:inline">
                  {isOfferPublished ? "Unpublish" : "Publish"} Offer
                </span>
              </OfficeButton>
            </Show>
            <Show if={!Boolean(currentUser?.employee?.accept_waiver_aggreement)}>
              <OfficeButton size="sm" onClick={() => setModal("agreement")}>
                <DocumentCheckIcon className="w-5 h-5 mr-2" />
                <span className="inline text-xs sm:hidden">Agree</span>
                <span className="hidden sm:inline">Agreement</span>
              </OfficeButton>
            </Show>
            <OfficeButton size="sm" onClick={() => setModal("send")}>
              <PaperAirplaneIcon className="w-5 h-5 mr-2" />
              <span className="inline text-xs sm:hidden">Send</span>
              <span className="hidden sm:inline">Send Emails</span>
            </OfficeButton>
            <OfficeButton size="sm" onClick={() => setModal("close")}>
              <LockClosedIcon className="w-5 h-5 mr-2" />
              <span className="inline text-xs sm:hidden">Close</span>
              <span className="hidden sm:inline">Close Offer</span>
            </OfficeButton>
          </>
        )}
      />
      <Tabs tabs={tabs} orderStatus={orderStatus} defaultTab="Overview" />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0 sm:py-6 overflow-hidden overflow-y-auto light flex">
          {!orderStatus && <OfferOverView />}
          {orderStatus === "participants" && <OfferParticipation isOfferPublished={isOfferPublished} />}
          {orderStatus === "associates" && <OfferAssociates />}
        </div>
      </div>
      <AddParticipantsContainer
        open={modal === "create"}
        setOpen={(val: boolean) => setModal(val ? "create" : undefined)}
      />
      <SendEmailsContainer
        open={modal === "send"}
        setOpen={(val: boolean) => setModal(val ? "send" : undefined)}
      />
      <CloseOfferContainer
        open={modal === "close"}
        setOpen={(val: boolean) => setModal(val ? "close" : undefined)}
      />
      <AcceptAgreementContainer
        open={modal === "agreement"}
        setOpen={(val: boolean) => setModal(val ? "agreement" : undefined)}
      />
      <PublishUnpublishOfferContainer
        open={modal === "publish"}
        setOpen={(val: boolean) => {
          setModal(val ? "publish" : undefined);
        }}
        offerId={offerId}
        isPublished={isOfferPublished}
        refetch={refetch}
      />
    </main>
  );
};

export default OfferPage;
